<template>
  <div class="login">
    <div class="login__image">
      <div class="container">
        <img class="image" src="@/assets/icons/Apoterkset.svg" alt="Apoteket" />
      </div>
    </div>
    <div class="login__content">
      <div class="container">
        <div class="login__content-head">
          <div class="login__content-head-lang">
            <LangChange />
          </div>
        </div>
        <div class="login__content-form">
          <Loading v-if="loading" />
          <div class="form">
            <div class="form-title">
              <p>{{ $t('login') }}</p>
            </div>
            <div class="form-inputs">
              <Input
                type="email"
                name="email"
                :label="$t('email')"
                :value="email"
                v-model="email"
                forName="email"
                validation="required|email"
                :validationMessages="{
                  required: $t('validation.required'),
                  email: $t('validation.email')
                }"
                @change="handleChange"
              />
              <Input
                type="password"
                name="password"
                :label="$t('password')"
                :value="password"
                v-model="password"
                @change="handleChange"
                forName="password"
                validation="required"
                :validationMessages="{
                  required: $t('validation.required')
                }"
              />
            </div>
            <div class="form-action">
              <div class="container-forgotPassword" v-if="false">
                <div
                  class="forgot-password"
                  @click="openForgotPassword"
                  v-if="forgotPassword === false"
                >
                  <p>{{ $t('forgotPassword') }}</p>
                </div>
              </div>
              <div class="form-error" v-show="status.error">
                <div class="error" role="alert">
                  {{ $t(status.message || 'error') }}
                </div>
              </div>
            </div>
            <div class="form-buttons">
              <Button
                id="login"
                class="button-log"
                variant="logIn"
                type="button"
                @click="signIn"
                :title="$t('login')"
                data-testid="loginButton"
              />
              <div class="login-microsoft">
                <i class="fa fa-windows" aria-hidden="true"></i>
                <Button
                  id="authenticateUsingProvider"
                  class="button-log"
                  variant="blackButton"
                  type="button"
                  @click="loginAD()"
                  :title="$t('loginWithMicrosoft')"
                  data-testid="loginloginWithMicrosoftButton"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapState, mapActions } from 'vuex';

import LangChange from '@/views/Language.vue';
import Loading from '@/views/Loading.vue';

import router from '@/router';
import { fallbackLocale } from '@/main';
import { InputFileEvent } from '@/types';

@Options({
  components: {
    Loading,
    LangChange
  },
  data() {
    return {
      email: null,
      password: null,
      loading: false,
      forgotPassword: false,
      boxId: ''
    };
  },
  computed: {
    ...mapState('account', ['status']),
    ...mapState('pharmacy', ['pharmacy']),
    ...mapState('account', ['user'])
  },
  methods: {
    ...mapActions('account', ['login', 'logout', 'loginWithMicrosoft']),
    handleChange(e: InputFileEvent) {
      this[e.target.name] = e.target.value;
    },
    async signIn() {
      this.loading = true;
      this.forgotPassword = false;

      if (this.email && this.password) {
        await this.login({
          email: this.email,
          password: this.password
        });

        if (this.boxId) {
          router.push({
            name: 'Lockers',
            params: {
              id: this.user?.pharmacyId,
              boxId: this.boxId
            }
          });
        }
        this.loading = false;
      }

      this.$i18n.locale = localStorage.getItem('locale') || fallbackLocale;
    },
    openForgotPassword() {
      this.forgotPassword = true;
      router.push({ name: 'ForgotPassword' });
    },
    loginAD() {
      this.loginWithMicrosoft();
    }
  },
  watch: {
    user() {
      this.boxId = this.pharmacy?.pickupBoxes[0];
    }
  }
})
export default class LoginComponent extends Vue {}
</script>

<style lang="scss" scoped>
.login {
  display: flex;
  .login__image {
    width: 50%;
    height: 100vh;
    .container {
      width: 100%;
      height: 100%;
      background-color: $light-grey;
      .image {
        height: 100%;
      }
    }
  }
  .login__content {
    width: 50%;
    height: 100vh;
    .container {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: $light-grey;
      .login__content-head {
        .login__content-head-lang {
          position: absolute;
          width: 172px;
          height: 42px;
          right: 32px;
          top: 32px;
          border-radius: 8px;
          background-color: $light-grey;
        }
      }
      .login__content-form {
        max-width: 416px;
        max-height: 345px;
        border-radius: 8px;
        .form {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 32px;
          border-radius: 8px;
          background: $white;
          .form-title {
            width: 352px;
            margin-bottom: 30px;
            p {
              font-family: 'Ubuntu-Medium';
              font-size: $twentyFourSize;
              line-height: $twentyEightSize;
              font-weight: 400;
            }
          }
          .form-inputs {
            width: 352px;
          }
          .form-action {
            display: flex;
            justify-content: space-between;
            width: 352px;
            margin-top: 16px;
            .forgot-password {
              cursor: pointer;
              p {
                font-size: $twelveSize;
                line-height: $fourteenSize;
                font-weight: 400;
                color: $main-green;
              }
            }
            .form-error {
              .error {
                background-color: $white;
                border: none;
                border-radius: 0;
                padding: 0;
                margin-bottom: 0;
                color: $messageError;
                font-size: $twelveSize;
                text-align: center;
              }
            }
          }
          .form-buttons {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            margin-top: 24px;
            .button-log {
              width: 170px;
            }
          }
          .login-microsoft {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 170px;
            border: 1px solid $label;
            border-radius: 8px;
            .fa {
              margin-left: 5px;
              cursor: pointer;
            }
            .button-log {
              width: 100%;
            }
          }
        }

        @media (min-width: 900px) {
          width: 100%;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.form-inputs {
  .input {
    &:last-child {
      margin-bottom: 0;
    }
    .formkit-outer {
      width: 352px;
      .formkit-wrapper {
        .formkit-inner {
          .formkit-input {
            &:hover {
              border: 1px solid $main-green;
            }
          }
        }
      }
    }
  }
}
</style>
